import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box, Typography, Paper, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Stack, CircularProgress, Alert,
  Divider, Chip, InputAdornment,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AccessTime as AccessTimeIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Event as EventIcon,
  MedicalServices as MedicalServicesIcon,
  TaskAlt as TaskAltIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

const API = process.env.REACT_APP_API;

const staffTheme = createTheme({
  palette: {
    primary: { main: "#006D77" },
    success: { main: "#16a34a" },
    background: { default: "#F0F9F9" },
  },
  typography: { fontFamily: "'DM Sans', 'Inter', sans-serif" },
  shape: { borderRadius: 12 },
  components: {
    MuiButton:    { styleOverrides: { root: { textTransform: "none", fontWeight: 700, borderRadius: 8 } } },
    MuiTextField: { defaultProps: { size: "small" } },
  },
});

const INFO_ROW = ({ icon, label, value, accent = "#006D77" }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1.1, borderBottom: "1px solid #f1f5f9" }}>
    <Box sx={{ color: accent, mt: 0.2, "& svg": { fontSize: 16 } }}>{icon}</Box>
    <Box>
      <Typography variant="caption" fontWeight={700} textTransform="uppercase"
        letterSpacing={0.5} color="text.secondary" fontSize={9.5} display="block">{label}</Typography>
      <Typography fontSize={13} fontWeight={600}>{value || "—"}</Typography>
    </Box>
  </Box>
);

export default function StaffUpdatePage() {
  const { bookingRef } = useParams();

  const [booking,  setBooking]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [saving,   setSaving]   = useState(false);
  const [done,     setDone]     = useState(false);
  const [form,     setForm]     = useState({
    actual_start_time:   "",
    actual_end_time:     "",
    time_taken_minutes:  "",
    completion_notes:    "",
    treatment_completed: true,
  });

  // ── Fetch booking by ref ───────────────────────────────────────────────────
  useEffect(() => {
    if (!bookingRef) return;
    fetch(`${API}/nursingphysiotherapy/staff/update/${bookingRef}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) setBooking(d.booking);
        else setError(d.error || "Booking not found");
      })
      .catch(() => setError("Cannot reach server. Please check your connection."))
      .finally(() => setLoading(false));
  }, [bookingRef]);

  const handleSubmit = async () => {
    if (!form.actual_start_time.trim()) { alert("Please enter the start time"); return; }
    if (!form.actual_end_time.trim())   { alert("Please enter the end time");   return; }

    setSaving(true);
    try {
      const r = await fetch(`${API}/nursingphysiotherapy/staff/update/${bookingRef}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(form),
      });
      const d = await r.json();
      if (d.success) setDone(true);
      else setError(d.error || "Submission failed. Please try again.");
    } catch { setError("Cannot reach server. Please try again."); }
    setSaving(false);
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <ThemeProvider theme={staffTheme}>
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2 }}>
        <CircularProgress sx={{ color: "#006D77" }} size={40} />
        <Typography color="text.secondary" fontSize={14}>Loading your assignment…</Typography>
      </Box>
    </ThemeProvider>
  );

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) return (
    <ThemeProvider theme={staffTheme}>
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
        <Paper sx={{ maxWidth: 420, width: "100%", borderRadius: 3, overflow: "hidden", textAlign: "center" }}>
          <Box sx={{ background: "linear-gradient(135deg, #dc2626, #991b1b)", px: 3, py: 3 }}>
            <CancelIcon sx={{ fontSize: 48, color: "#fff", mb: 1 }} />
            <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: 18 }}>Link Error</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary" fontSize={14} mb={2}>{error}</Typography>
            <Typography fontSize={12} color="text.secondary">
              Please contact your supervisor if you believe this is a mistake.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </ThemeProvider>
  );

  // ── Already completed ──────────────────────────────────────────────────────
  if (booking?.status === "completed" && !done) return (
    <ThemeProvider theme={staffTheme}>
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
        <Paper sx={{ maxWidth: 420, width: "100%", borderRadius: 3, overflow: "hidden", textAlign: "center" }}>
          <Box sx={{ background: "linear-gradient(135deg, #16a34a, #166534)", px: 3, py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: "#fff", mb: 1 }} />
            <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: 18 }}>Already Completed</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary" fontSize={14}>
              This booking <strong>{bookingRef}</strong> has already been marked as completed.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </ThemeProvider>
  );

  // ── Cancelled ──────────────────────────────────────────────────────────────
  if (booking?.status === "cancelled") return (
    <ThemeProvider theme={staffTheme}>
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
        <Paper sx={{ maxWidth: 420, width: "100%", borderRadius: 3, overflow: "hidden", textAlign: "center" }}>
          <Box sx={{ background: "linear-gradient(135deg, #d97706, #b45309)", px: 3, py: 3 }}>
            <CancelIcon sx={{ fontSize: 48, color: "#fff", mb: 1 }} />
            <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: 18 }}>Booking Cancelled</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary" fontSize={14}>
              This booking has been cancelled. No action required.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </ThemeProvider>
  );

  // ── Success ────────────────────────────────────────────────────────────────
  if (done) return (
    <ThemeProvider theme={staffTheme}>
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
        <Paper sx={{ maxWidth: 460, width: "100%", borderRadius: 3, overflow: "hidden", textAlign: "center" }}>
          <Box sx={{ background: "linear-gradient(135deg, #16a34a, #166534)", px: 3, py: 4 }}>
            <Box sx={{ width: 80, height: 80, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
              <TaskAltIcon sx={{ fontSize: 48, color: "#fff" }} />
            </Box>
            <Typography sx={{ fontWeight: 900, color: "#fff", fontSize: 22, fontFamily: "'DM Sans',sans-serif" }}>
              Submitted Successfully!
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: 13, mt: 0.75 }}>
              Your completion report has been sent to the admin.
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Alert severity="success" sx={{ borderRadius: 2, mb: 2, textAlign: "left" }}>
              Booking <strong>{bookingRef}</strong> marked as{" "}
              <strong>{form.treatment_completed ? "Completed" : "Partially Completed"}</strong>.
              {form.time_taken_minutes && ` Time taken: ${form.time_taken_minutes} mins.`}
            </Alert>
            <Typography fontSize={13} color="text.secondary">
              Thank you for your service! The admin has been notified.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </ThemeProvider>
  );

  // ── Main form ──────────────────────────────────────────────────────────────
  const isNursing   = booking?.category === "nursing";
  const accentColor = isNursing ? "#006D77" : "#b8654a";
  const gradient    = isNursing
    ? "linear-gradient(135deg, #006D77, #00949f)"
    : "linear-gradient(135deg, #b8654a, #8B3E2F)";

  return (
    <ThemeProvider theme={staffTheme}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ maxWidth: 520, mx: "auto" }}>

          {/* ── Header card ── */}
          <Paper sx={{ borderRadius: 3, overflow: "hidden", mb: 2.5 }}>
            <Box sx={{ background: gradient, px: 3, py: 3, position: "relative", overflow: "hidden" }}>
              <Box sx={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.07)" }} />
              <Box sx={{ position: "absolute", bottom: -20, left: -10, width: 80, height: 80, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.06)" }} />
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, mb: 0.5 }}>
                  Ayuja Healthcare · Staff Portal
                </Typography>
                <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: { xs: 18, sm: 22 }, mb: 0.5 }}>
                  {booking.service_name}
                </Typography>
                <Box sx={{ display: "inline-block", bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 12, fontWeight: 800, px: 1.5, py: 0.3, borderRadius: 1.5, fontFamily: "monospace", mb: 1.5 }}>
                  {booking.booking_ref}
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip label={isNursing ? "🏥 Nursing" : "💪 Physiotherapy"} size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700, fontSize: 11 }} />
                  <Chip label="Pending Completion" size="small"
                    sx={{ bgcolor: "rgba(253,230,138,0.25)", color: "#FDE68A", fontWeight: 700, fontSize: 11 }} />
                </Box>
              </Box>
            </Box>

            {/* Patient & appointment info */}
            <Box sx={{ px: 2.5, pt: 2, pb: 0.5 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 800, color: accentColor, textTransform: "uppercase", letterSpacing: 0.6 }}>
                Assignment Details
              </Typography>
            </Box>
            <Box sx={{ px: 2.5, pb: 2 }}>
              <INFO_ROW icon={<PersonIcon />}      label="Patient"  value={booking.patient_name}    accent={accentColor} />
              <INFO_ROW icon={<PhoneIcon />}       label="Phone"    value={booking.patient_phone}   accent={accentColor} />
              <INFO_ROW icon={<LocationOnIcon />}  label="Address"  value={booking.patient_address} accent={accentColor} />
              <INFO_ROW icon={<EventIcon />}       label="Date"     value={booking.scheduled_date}  accent={accentColor} />
              <INFO_ROW icon={<ScheduleIcon />}    label="Time"     value={booking.scheduled_time}  accent={accentColor} />
              {booking.notes && (
                <INFO_ROW icon={<NotesIcon />} label="Patient Notes" value={booking.notes} accent={accentColor} />
              )}
            </Box>
          </Paper>

          {/* ── Completion form ── */}
          <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ background: "linear-gradient(135deg, #16a34a, #166534)", px: 3, py: 2.5 }}>
              <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: 16 }}>
                ✅ Submit Completion Report
              </Typography>
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.7)", mt: 0.3 }}>
                Fill in the details after completing the service
              </Typography>
            </Box>

            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={2.5}>

                {/* Time fields */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField fullWidth label="Actual Start Time *"
                    placeholder="e.g. 10:00 AM"
                    value={form.actual_start_time}
                    onChange={e => setForm(f => ({ ...f, actual_start_time: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><ScheduleIcon sx={{ fontSize: 16, color: "#16a34a" }} /></InputAdornment> }} />
                  <TextField fullWidth label="Actual End Time *"
                    placeholder="e.g. 11:30 AM"
                    value={form.actual_end_time}
                    onChange={e => setForm(f => ({ ...f, actual_end_time: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><ScheduleIcon sx={{ fontSize: 16, color: "#16a34a" }} /></InputAdornment> }} />
                </Stack>

                {/* Minutes taken */}
                <TextField fullWidth label="Total Time Taken (minutes)"
                  type="number"
                  placeholder="e.g. 90"
                  value={form.time_taken_minutes}
                  onChange={e => setForm(f => ({ ...f, time_taken_minutes: e.target.value }))}
                  helperText="Enter the total duration of the service in minutes"
                  InputProps={{ startAdornment: <InputAdornment position="start"><AccessTimeIcon sx={{ fontSize: 16, color: "#16a34a" }} /></InputAdornment> }} />

                {/* Treatment status */}
                <FormControl fullWidth>
                  <InputLabel>Treatment Status *</InputLabel>
                  <Select value={form.treatment_completed} label="Treatment Status *"
                    onChange={e => setForm(f => ({ ...f, treatment_completed: e.target.value }))}>
                    <MenuItem value={true}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 18 }} />
                        <Box>
                          <Typography fontSize={13} fontWeight={700}>Completed Successfully</Typography>
                          <Typography fontSize={11} color="text.secondary">Full service was delivered as planned</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value={false}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <MedicalServicesIcon sx={{ color: "#d97706", fontSize: 18 }} />
                        <Box>
                          <Typography fontSize={13} fontWeight={700}>Partially Completed</Typography>
                          <Typography fontSize={11} color="text.secondary">Service was partially delivered — explain in notes</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Notes */}
                <TextField fullWidth label="Outcome Notes / Observations"
                  multiline rows={4}
                  placeholder="Describe the treatment outcome, patient condition, any follow-up required, medications given, exercises taught…"
                  value={form.completion_notes}
                  onChange={e => setForm(f => ({ ...f, completion_notes: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start"><NotesIcon sx={{ fontSize: 16, color: "#16a34a", mt: 0.5 }} /></InputAdornment> }} />

                {/* Preview summary */}
                {(form.actual_start_time || form.actual_end_time || form.time_taken_minutes) && (
                  <Box sx={{ bgcolor: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 2, p: 1.75 }}>
                    <Typography fontSize={12} fontWeight={700} color="#16a34a" mb={0.75}>
                      📋 Summary Preview
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap gap={1}>
                      {form.actual_start_time && (
                        <Box><Typography fontSize={10} color="text.secondary" fontWeight={700}>START</Typography>
                          <Typography fontSize={13} fontWeight={700}>{form.actual_start_time}</Typography></Box>
                      )}
                      {form.actual_end_time && (
                        <Box><Typography fontSize={10} color="text.secondary" fontWeight={700}>END</Typography>
                          <Typography fontSize={13} fontWeight={700}>{form.actual_end_time}</Typography></Box>
                      )}
                      {form.time_taken_minutes && (
                        <Box><Typography fontSize={10} color="text.secondary" fontWeight={700}>DURATION</Typography>
                          <Typography fontSize={13} fontWeight={700}>{form.time_taken_minutes} mins</Typography></Box>
                      )}
                      <Box><Typography fontSize={10} color="text.secondary" fontWeight={700}>STATUS</Typography>
                        <Typography fontSize={13} fontWeight={700} color={form.treatment_completed ? "#16a34a" : "#d97706"}>
                          {form.treatment_completed ? "✅ Completed" : "⚠️ Partial"}
                        </Typography></Box>
                    </Stack>
                  </Box>
                )}

                <Divider />

                {/* Submit */}
                <Button variant="contained" color="success" size="large" fullWidth
                  onClick={handleSubmit} disabled={saving}
                  startIcon={saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <TaskAltIcon />}
                  sx={{ py: 1.5, fontSize: 15, fontWeight: 800, borderRadius: 2,
                    background: saving ? undefined : "linear-gradient(135deg, #16a34a, #166534)",
                    boxShadow: "0 4px 14px rgba(22,163,74,0.3)",
                  }}>
                  {saving ? "Submitting…" : "Submit Completion Report"}
                </Button>

                <Typography fontSize={11} color="text.secondary" textAlign="center">
                  By submitting, the admin will be notified immediately. This action cannot be undone.
                </Typography>
              </Stack>
            </Box>
          </Paper>

          {/* Footer */}
          <Box sx={{ textAlign: "center", mt: 3, pb: 4 }}>
            <Typography fontSize={12} color="text.secondary">
              Ayuja Healthcare · Staff Portal · {bookingRef}
            </Typography>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}